<template>
  <div class="dashboard-view">
    <BaseContainer
      :active-spacer="true"
      base-container-classes="pb-0"
    >
      <template v-slot:header-title>
        <v-skeleton-loader
          v-if="!currentScreenData && (isFetchingScreen || !dynamicNavigationFetched)"
          type="heading"
          width="520"
        />
        <template v-else-if="currentScreenData">
          {{ currentScreenData.title }}
        </template>
      </template>
      <template v-slot:header-title-description>
        <v-skeleton-loader
          v-if="!currentScreenData && (isFetchingScreen || !dynamicNavigationFetched)"
          type="paragraph"
          width="820"
          class="my-4"
        />
        <template v-else-if="currentScreenData">
          {{ currentScreenData && currentScreenData.description }}
        </template>
      </template>
      <template v-slot:header-actions>
        <v-menu
          v-if="dynamicNavigationFetched && params.length"
          v-model="filtersMenuOpen"
          :close-on-content-click="false"
          offset-y
          left
        >
          <template v-slot:activator="{ on, attrs }">
            <v-badge
              :value="!allFilterValuesFilled"
              color="warning"
              dot
              overlap
            >
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-filter-variant</v-icon>
              </v-btn>
            </v-badge>
          </template>
          <v-card class="pa-4 dashboard-view__filters-card">
            <div
              class="app-align-items-center"
              style="flex-wrap: wrap;"
            >
              <template v-for="param in params">
                <BaseDatePicker
                  v-if="param.type === 'date'"
                  :key="param.name"
                  :name-placeholder="paramLabel(param)"
                  :date-value="filterValues[param.name]"
                  :max-width="118"
                  locale="pt-br"
                  time-zone="UTC"
                  class="dashboard-view__filter-field"
                  @on-input-date-picker="(value) => { filterValues[param.name] = value }"
                />
                <BaseInput
                  v-else
                  :key="param.name"
                  v-model="filterValues[param.name]"
                  :label="paramLabel(param)"
                  :type="param.type === 'number' ? 'number' : 'text'"
                  class="my-4 ml-4 dashboard-view__filter-field"
                />
              </template>
              <BaseButton
                type="text"
                color="primary"
                :is-disabled="!allFilterValuesFilled"
                @click="fetchScreen(); filtersMenuOpen = false"
              >
                Buscar
              </BaseButton>
            </div>
          </v-card>
        </v-menu>
      </template>
      <DashboardScreen
        v-if="currentScreenData"
        class="my-2"
        :screen="currentScreen"
        hide-header
        @row-detail="onRowDetail"
        @input="onDrawerInput"
        @filter-change="onFilterChange"
        @update:options="onTableOptionsChange"
        @submit="onFilterSubmit"
      />
      <div
        v-else-if="showPendingCard"
        class="d-flex justify-center dashboard-view__pending-filters"
        style="height: calc(100vh - 180px)"
      >
        <div class="text-center align-self-center px-6">
          <v-icon
            size="40"
            color="grey"
          >
            mdi-filter-variant
          </v-icon>
          <template v-if="!allFilterValuesFilled">
            <p class="mt-3 mb-4">
              Esta tela tem filtros obrigatórios pendentes. Preencha-os para continuar.
            </p>
            <BaseButton
              color="primary"
              @click="filtersMenuOpen = true"
            >
              Preencher filtros
            </BaseButton>
          </template>
          <template v-else>
            <p class="mt-3 mb-4">
              Os filtros já estão preenchidos. Clique em "Buscar" para carregar esta tela.
            </p>
            <BaseButton
              color="primary"
              @click="fetchScreen()"
            >
              Buscar
            </BaseButton>
          </template>
        </div>
      </div>
      <BaseDetailDrawer
        v-for="entry in openIndependentDrawersList"
        :key="entry.id"
        :value="true"
        :title="entry.row.detail.props.title || ''"
        :subtitle="entry.row.detail.props.subtitle || ''"
        :width="entry.row.detail.props.width"
        @input="(value) => onIndependentDrawerInput(entry.id, value)"
      >
        <DashboardNodeRenderer
          v-for="(child, index) in (entry.row.detail.children || [])"
          :key="child.id || index"
          :node="child"
          @row-detail="onRowDetail"
          @filter-change="onFilterChange"
          @update:options="onTableOptionsChange"
          @submit="onFilterSubmit"
        />
      </BaseDetailDrawer>
    </BaseContainer>
    <BaseSnackbar
      :active-snackbar="feedback"
      :type-snackbar="feedbackColor"
      :text-snackbar="feedbackMessage"
      @closeSnackbar="feedback = !feedback"
    />
  </div>
</template>

<script>
import DashboardScreen from '../../Dashboard/DashboardScreen.vue'
import DashboardNodeRenderer from '../../Dashboard/DashboardNodeRenderer.vue'
import BaseDetailDrawer from '../../Dashboard/BaseDetailDrawer.vue'
import BaseDatePicker from '../../BaseDatePicker'
import { mapActions, mapState, mapGetters } from "vuex"
import BaseSnackbarConfiguration from '../../../mixins/BaseSnackbarConfiguration'
import DashboardIndependentDrawers from '../../../mixins/DashboardIndependentDrawers'
import BaseSnackbar from '../../BaseSnackbar'

export default {
  name: 'DashboardView',
  components: { DashboardScreen, DashboardNodeRenderer, BaseDetailDrawer, BaseDatePicker, BaseSnackbar },
  mixins: [BaseSnackbarConfiguration, DashboardIndependentDrawers],
  data() {
    return {
      filterValues: {},
      currentScreenData: null,
      filtersMenuOpen: false,
      isFetchingScreen: false
    }
  },
  computed: {
    ...mapState({
      programInitiator: state => state.platformProgram.programInitiator,
      dynamicNavigationFetched: state => state.dynamicNavigation.fetched
    }),
    ...mapGetters(['paramsForView']),
    baseScreen() {
      return this.currentScreenData
    },
    params() {
      return this.paramsForView(this.$route.params.view).filter((param) => param.name !== 'program_id')
    },
    requiredParams() {
      return this.params.filter((param) => param.required !== false)
    },
    allFilterValuesFilled() {
      return this.requiredParams.every((param) => {
        const value = this.filterValues[param.name]
        return value !== null && value !== undefined && value !== ''
      })
    },
    showPendingCard() {
      return this.dynamicNavigationFetched && !this.isFetchingScreen && !this.currentScreenData
    }
  },
  watch: {
    '$route.params.view'() {
      this.resetScreenState()
      if (!this.dynamicNavigationFetched) {
        return
      }
      this.initFilterValues()
      this.maybeAutoFetch()
    },
    dynamicNavigationFetched(value) {
      if (value) {
        this.initFilterValues()
        this.maybeAutoFetch()
      }
    }
  },
  created() {
    if (this.dynamicNavigationFetched) {
      this.initFilterValues()
      this.maybeAutoFetch()
    }
  },
  methods: {
    ...mapActions(["apiGetScreen", "apiGetProgramInfo"]),
    paramLabel(param) {
      const label = param.label || param.name
      return param.required !== false ? `${label} *` : label
    },
    resetScreenState() {
      this.currentScreenData = null
      this.filtersMenuOpen = false
      this.openRow = null
      this.openIndependentDrawers = {}
      this.tableFilterValues = {}
      this.tableSearchValues = {}
      this.tablePages = {}
      this.tableItemsPerPage = {}
    },
    initFilterValues() {
      const values = {}
      this.params.forEach((param) => {
        values[param.name] = param.type === 'date' ? new Date().toISOString().slice(0, 10) : ''
      })
      this.filterValues = values
    },
    maybeAutoFetch() {
      if (this.allFilterValuesFilled) {
        this.fetchScreen()
      } else {
        this.filtersMenuOpen = true
      }
    },
    async fetchScreen() {
      this.isFetchingScreen = true
      try {
        if (!this.programInitiator) {
          await this.apiGetProgramInfo()
        }
        const result = await this.apiGetScreen({
          view: this.$route.params.view,
          programId: this.programInitiator,
          params: { ...this.filterValues, ...this.tableQueryParams }
        })
        this.currentScreenData = result
        this.resyncOpenDrawers()
      } catch (error) {
        const missingParams = error.response && error.response.status === 400 && error.response.data && error.response.data.missingParams
        this.showFeedback({
          color: "error",
          message: Array.isArray(missingParams) && missingParams.length
            ? `Parâmetros obrigatórios faltando: ${missingParams.join(', ')}.`
            : 'Erro ao buscar tela.',
        })
      } finally {
        this.isFetchingScreen = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.dashboard-view {
  &__filters-card {
    min-width: 320px;
    max-width: 90vw;
  }

  &__filter-field {
    min-width: 160px;
  }

  &__pending-filters {
    color: var(--ds-color-black-055);
  }
}
</style>
